'use client'

import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  Navigation,
  Divider,
  Text,
  Container,
  Icon,
  Spacer,
  ColorCode,
} from '@vinted/web-ui'
import { InfoCircle16, Redirect16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { redirectToPageInNewTab } from '@marketplace-web/shared/browser-util'
import { VanCreativeModel, VanPlacementConfigModel } from '@marketplace-web/ads/ads-data'

import AdInfo from '../../AdInfo'
import useAdEventTracking from '../../../../hooks/useAdEventTracking'
import useFetchVanCreative from '../../../../hooks/useFetchVanCreative'
import { VAN_CAMPAIGN_PRIVACY_POLICY_URL, PRIVACY_POLICY_URL } from '../../../../constants/routes'

type Props = {
  isOpen: boolean
  config: VanPlacementConfigModel
  onClose: () => void
  creative: VanCreativeModel
}

const IFRAME_BUTTON_CLOSE_DELAY = 100
const RETRY_INTERVAL_MS = 200

const VanOfferContent = ({ isOpen, config, creative, onClose }: Props) => {
  const [isAdRendered, setIsAdRendered] = useState(false)

  const { viewableImpressionRef, impressionRef } = useAdEventTracking({
    isAdRendered,
    placementConfig: config,
    customPlacementId: config.placementId,
  })

  const translate = useTranslate('vinted_ads_network.ad_offer')

  const { iframeContent } = useFetchVanCreative({ config, creative })

  const onOfferButtonClick = () => {
    redirectToPageInNewTab(creative.clickThroughUrl)

    onClose()
  }

  useEffect(() => {
    if (!iframeContent || !isOpen) return

    setIsAdRendered(true)
  }, [iframeContent, isOpen])

  // TODO: if this works can be moved to separate hook
  useEffect(() => {
    // If there is no iframeContent there is no point attaching the listener
    if (!iframeContent) return undefined

    let timeoutId: NodeJS.Timeout | null = null

    const clearExistingTimeout = () => {
      if (!timeoutId) return
      clearTimeout(timeoutId)
      timeoutId = null
    }

    const tryAttachListener = () => {
      const iframe = document.getElementById('van-offer-iframe') as HTMLIFrameElement | null

      if (!iframe || !iframe.contentWindow) {
        // Adds this on close delay to ensure that iframe button redirects user
        // to the correct URL before the dialog closes
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      const iframeImage = iframe.contentWindow.document.getElementById('van-creative')

      if (!iframeImage) {
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      iframeImage.addEventListener('click', () => {
        setTimeout(() => {
          onClose()
        }, IFRAME_BUTTON_CLOSE_DELAY)
      })

      clearExistingTimeout()
    }

    tryAttachListener()

    return clearExistingTimeout
  }, [iframeContent, onClose])

  if (!iframeContent || !isOpen || !config?.id) return null

  return (
    <Dialog show={isOpen} closeOnOverlay defaultCallback={onClose}>
      <div ref={viewableImpressionRef}>
        <div ref={impressionRef}>
          <Navigation
            body={translate('header_title')}
            right={
              <Button
                inline
                iconName={X24}
                iconColor={ColorCode.GreyscaleLevel2}
                styling={Button.Styling.Flat}
                testId="van-offer-navigation-close"
                aria={{ 'aria-label': translate('a11y.actions.close') }}
                onClick={onClose}
              />
            }
          />
          <Divider />
          <Container>
            <div className="van-offer-container">
              <div data-testid="van-offer-slot" id="van-offer-slot">
                <iframe
                  id="van-offer-iframe"
                  title="van-offer-iframe"
                  width={creative.size.width}
                  height={creative.size.height}
                  className="van-offer-iframe"
                  srcDoc={iframeContent}
                />
              </div>
              <Spacer size={Spacer.Size.Regular} />
              <div style={{ width: creative.size.width }}>
                <Text
                  as="h2"
                  type={Text.Type.SectionHeading}
                  text={translate('title')}
                  alignment={Text.Alignment.Center}
                  width={Text.Width.Parent}
                />
                <Text
                  as="p"
                  text={translate('subtitle')}
                  type={Text.Type.Subtitle}
                  alignment={Text.Alignment.Center}
                  width={Text.Width.Parent}
                />
                <Spacer size={Spacer.Size.Medium} />
                <Button
                  text={translate('button')}
                  iconPosition={Button.IconPosition.Right}
                  iconName={Redirect16}
                  styling={Button.Styling.Filled}
                  size={Button.Size.Medium}
                  theme="primary"
                  onClick={onOfferButtonClick}
                  testId="van-offer-claim-button"
                />
              </div>
            </div>
          </Container>
          <Container>
            <div className="van-offer-info-container">
              <a
                href={VAN_CAMPAIGN_PRIVACY_POLICY_URL(config.id)}
                className="van-offer-info"
                target="_blank"
                rel="noreferrer"
              >
                <AdInfo />
                <Icon name={InfoCircle16} color={ColorCode.GreyscaleLevel2} />
              </a>
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                <Text
                  as="p"
                  type={Text.Type.Caption}
                  theme="muted"
                  text={translate('privacy_policy')}
                  underline
                />
              </a>
            </div>
          </Container>
        </div>
      </div>
    </Dialog>
  )
}

export default VanOfferContent
